import { useEffect, useState } from 'react';
import { toDanishPath, toEnglishPath } from '../lib/routeMapping';

const DEFAULT_IMAGE = 'https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/scandinavian-forest-hero.jpg';
const DEFAULT_SITE_NAME = 'Nordisk Psykiatri';

// In-memory cache for CMS SEO settings so we only fetch once across route navigation
let seoCache = null;
let seoPromise = null;

async function fetchCmsSeo() {
  if (seoCache) return seoCache;
  if (seoPromise) return seoPromise;
  seoPromise = (async () => {
    try {
      const res = await fetch('/_api/public/content/seo_indstillinger');
      if (res.ok) {
        const data = await res.json();
        const map = {};
        const items = Array.isArray(data) ? data : (data.items || []);
        items.forEach((item) => {
          const path = item.side_sti || item.slug;
          if (path) {
            map[path] = item;
          }
        });
        seoCache = map;
        return map;
      }
    } catch {
      // Fallback seamlessly if CMS request fails
    }
    return {};
  })();
  return seoPromise;
}

/**
 * useDocumentMetadata
 * Opdaterer dynamisk sidetitel, meta description, Open Graph tags, Twitter Card tags,
 * canonical link, robots samt html[lang] for den aktive side.
 * Henter automatisk fra CMS-samlingen "seo_indstillinger", hvis kunden har tilpasset
 * meta-titler eller meta-beskrivelser der.
 *
 * Kan kaldes på to måder:
 * 1) useDocumentMetadata(title, description, options)
 * 2) useDocumentMetadata({ title, description, image, type, noIndex, canonicalUrl, lang })
 */
export default function useDocumentMetadata(titleOrOptions, maybeDescription, maybeOptions) {
  const isObject = typeof titleOrOptions === 'object' && titleOrOptions !== null;
  
  const rawTitle = isObject ? titleOrOptions.title : titleOrOptions;
  const rawDesc = isObject ? titleOrOptions.description : maybeDescription;
  const options = isObject ? titleOrOptions : (maybeOptions || {});

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const isEnPath = currentPath === '/en' || currentPath.startsWith('/en/');
  const lang = options.lang || (isEnPath ? 'en' : 'da');
  const image = options.image || DEFAULT_IMAGE;
  const ogType = options.type || 'website';
  const noIndex = Boolean(options.noIndex);
  const explicitCanonical = options.canonicalUrl;

  const [cmsSeo, setCmsSeo] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchCmsSeo().then((map) => {
      if (!mounted || !map) return;
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const cmsLookup = toDanishPath(path);
      const matched = map[path] || map[cmsLookup] || map[cmsLookup.replace(/\/$/, '') || '/'];
      if (matched) {
        setCmsSeo(matched);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Check if CMS overrides title and description for this route
  const getCmsLocalized = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (lang === 'en') {
      return field.en || field.sv || field.da || '';
    }
    return field.da || field.en || field.sv || '';
  };

  const cmsTitle = cmsSeo?.meta_titel ? getCmsLocalized(cmsSeo.meta_titel) : '';
  const cmsDesc = cmsSeo?.meta_beskrivelse ? getCmsLocalized(cmsSeo.meta_beskrivelse) : '';

  const title = (cmsTitle || (typeof rawTitle === 'string' ? rawTitle.trim() : '')).trim();
  const description = (cmsDesc || (typeof rawDesc === 'string' ? rawDesc.trim() : '')).trim();

  useEffect(() => {
    // Opdater sidetitel
    if (title) {
      document.title = title;
    }

    // Opdater HTML lang attribut
    if (lang) {
      document.documentElement.lang = lang;
    }

    // Helper til at oprette eller opdatere meta tags
    const setMetaTag = (selector, attributeName, attributeValue, content) => {
      if (!content) return;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attributeName, attributeValue);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // Meta description
    if (description) {
      setMetaTag('meta[name="description"]', 'name', 'description', description);
    }

    // Robots tag (f.eks. ved 404 og 403)
    let robotsEl = document.querySelector('meta[name="robots"]');
    if (noIndex) {
      if (!robotsEl) {
        robotsEl = document.createElement('meta');
        robotsEl.setAttribute('name', 'robots');
        document.head.appendChild(robotsEl);
      }
      robotsEl.setAttribute('content', 'noindex, nofollow');
    } else {
      if (robotsEl) {
        robotsEl.setAttribute('content', 'index, follow');
      }
    }

    // Helper til at oprette eller opdatere link tags
    const setLinkTag = (rel, hreflang, href) => {
      if (!href) return;
      const selector = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]:not([hreflang])`;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        if (hreflang) el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    // Canonical link & hreflang alternates
    const domain = 'https://www.nordiskpsykiatri.dk';
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const isEn = lang === 'en' || path === '/en' || path.startsWith('/en/');

    const danishPath = toDanishPath(path);
    const englishPath = toEnglishPath(path);

    const daUrl = `${domain}${danishPath}`;
    const enUrl = `${domain}${englishPath}`;

    // Self-canonical: English page points to itself (/en/...), Danish points to itself (/...)
    let selfCanonical = isEn ? enUrl : daUrl;
    if (explicitCanonical) {
      if (isEn) {
        selfCanonical = toEnglishPath(explicitCanonical.replace(domain, ''));
        selfCanonical = `${domain}${selfCanonical}`;
      } else {
        selfCanonical = toDanishPath(explicitCanonical.replace(domain, ''));
        selfCanonical = `${domain}${selfCanonical}`;
      }
    }

    setLinkTag('canonical', null, selfCanonical);
    setLinkTag('alternate', 'da', daUrl);
    setLinkTag('alternate', 'en', enUrl);
    setLinkTag('alternate', 'x-default', daUrl);

    // Open Graph
    if (title) {
      setMetaTag('meta[property="og:title"]', 'property', 'og:title', title);
    }
    if (description) {
      setMetaTag('meta[property="og:description"]', 'property', 'og:description', description);
    }
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', ogType);
    setMetaTag('meta[property="og:site_name"]', 'property', 'og:site_name', DEFAULT_SITE_NAME);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', selfCanonical);
    if (image) {
      setMetaTag('meta[property="og:image"]', 'property', 'og:image', image);
    }
    const locale = isEn ? 'en_GB' : 'da_DK';
    setMetaTag('meta[property="og:locale"]', 'property', 'og:locale', locale);

    // Twitter Card tags
    setMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    if (title) {
      setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    }
    if (description) {
      setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    }
    if (image) {
      setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', image);
    }
  }, [title, description, image, ogType, noIndex, explicitCanonical, lang]);
}
